/**
 * Панель выбора колонок (§4.2).
 *
 * Делает три вещи: показывает и прячет колонки, задаёт их порядок и заводит
 * новые текстовые столбцы.
 *
 * Порядок меняется перетаскиванием: группы — плашками наверху (как пилюли
 * фильтров), колонки — строками внутри своей группы. Чекбокс и название
 * бренда не двигаются: на них держится закреплённая слева зона.
 * Перетаскивание дублируется клавишами Alt+↑/Alt+↓ — мышь не единственный
 * способ работать с таблицей (§13).
 */
import { useState } from 'react'
import type { KeyboardEvent, PointerEvent as ReactPointerEvent } from 'react'
import { Link } from 'react-router-dom'
import type { FieldScope, FieldType } from '@shared/types.ts'
import { Button, Checkbox, FieldRow, IconPlus, IconTrash, Input, Popover, useDragSort } from '../../components/index.js'
import { useApp } from '../../store/app-store.js'
import {
  FIXED_HEAD, groupOrder as defaultGroupOrder, groupTitles, type ColumnDef, type ColumnGroupCode,
} from './table/columns.js'
import { applyOrder, moved } from './table/columnOrder.js'
import { RemoveColumnDialog, type RemoveTarget } from './table/RemoveColumn.js'
import { tableTexts as t } from './texts.table.js'
import './ColumnsMenu.css'

export interface ColumnsMenuProps {
  open: boolean
  anchor: HTMLElement | null
  onClose: () => void
  catalog: ColumnDef[]
  visible: ReadonlySet<string>
  hiddenByWidth: readonly string[]
  collapsedGroups: ReadonlySet<ColumnGroupCode>
  onToggleColumn: (code: string, on: boolean) => void
  onToggleGroup: (group: ColumnGroupCode) => void
  onReset: () => void
  /** Порядок групп: применяется сразу, хранится в личных настройках. */
  groupOrder: readonly string[]
  onGroupOrder: (groups: string[]) => void
  /** Порядок колонок внутри группы. */
  columnOrder: Record<string, string[]>
  onColumnOrder: (group: string, codes: string[]) => void
  /** Поле заведено — каталог колонок нужно перечитать. */
  onFieldCreated: (code: string) => void
  /** Перенести колонку в другой блок. */
  onMoveColumn: (code: string, group: string) => void
}

export function ColumnsMenu(props: ColumnsMenuProps) {
  const {
    open, anchor, onClose, catalog, visible, hiddenByWidth, collapsedGroups,
    onToggleColumn, onToggleGroup, onReset, groupOrder, onGroupOrder,
    columnOrder, onColumnOrder, onFieldCreated, onMoveColumn,
  } = props

  const [adding, setAdding] = useState(false)
  /* Что удаляем: колонку или целый блок. Диалог спросит дважды. */
  const [removing, setRemoving] = useState<RemoveTarget | null>(null)

  const user = useApp((state) => state.bootstrap?.user ?? null)
  const mayAddColumn = Boolean(user?.permissions.includes('fields.manage'))
  const mayRemove = mayAddColumn

  const titles = groupTitles(catalog)
  const groups: ColumnGroupCode[] = applyOrder(groupOrder, defaultGroupOrder(catalog))

  const membersOf = (group: ColumnGroupCode): ColumnDef[] =>
    applyOrder(
      columnOrder[group] ?? [],
      catalog.filter((column) => column.group === group && column.optional).map((column) => column.code),
    ).map((item) => catalog.find((column) => column.code === item))
      .filter((column): column is ColumnDef => column !== undefined)

  /**
   * Плашка есть у группы с именем, в которой действительно есть колонки.
   * Иначе в списке висели «Приоритет» и «Поля», а их секций ниже не было:
   * перетаскивать можно было то, чего в таблице нет.
   */
  const shown = groups.filter((group) => (
    titles[group] ? membersOf(group).length > 0 : false
  ) || group === 'fields')
  const shownSet = new Set<string>(shown)

  /* Плашки блоков: тащим сами, с наклоном и расступающимися соседями. */
  const plates = useDragSort({
    items: groups,
    isFixed: (code) => !shownSet.has(code),
    onDrop: (order) => onGroupOrder(order),
  })

  return (
    <Popover
      open={open}
      anchor={anchor}
      onClose={onClose}
      role="dialog"
      placement="bottom-end"
      minWidth={320}
      maxWidth={360}
      focusOnOpen
    >
      <div className="columns-menu">
        <div className="columns-menu__head">
          <span className="t-col">{t.columnsMenu.title}</span>
          {mayAddColumn ? (
            <button
              type="button"
              className="columns-menu__add t-meta"
              aria-expanded={adding}
              onClick={() => setAdding((previous) => !previous)}
            >
              <IconPlus size={16} />
              {t.columnsMenu.addColumn}
            </button>
          ) : null}
        </div>

        {adding ? (
          <NewColumnForm
            catalog={catalog}
            onCancel={() => setAdding(false)}
            onCreated={(code) => {
              setAdding(false)
              onFieldCreated(code)
            }}
          />
        ) : null}

        {shown.map((group) => (
          <GroupSection
            key={group}
            group={group}
            title={titles[group] ?? ''}
            members={membersOf(group)}
            visible={visible}
            hiddenByWidth={hiddenByWidth}
            collapsed={collapsedGroups.has(group)}
            mayRemove={mayRemove}
            plateRef={plates.register(group)}
            dragging={plates.dragging === group}
            onPlatePointerDown={(event) => plates.onPointerDown(group, event)}
            onNudge={(delta) => nudgeGroup(group, delta)}
            onToggleColumn={onToggleColumn}
            onToggleGroup={() => onToggleGroup(group)}
            onColumnOrder={onColumnOrder}
            onMoveColumn={onMoveColumn}
            onRemove={setRemoving}
          />
        ))}

        <div className="columns-menu__foot">
          <Button variant="ghost" size="s" onClick={onReset}>{t.columnsMenu.reset}</Button>
        </div>
      </div>

      {removing ? (
        <RemoveColumnDialog target={removing} onClose={() => setRemoving(null)} />
      ) : null}
    </Popover>
  )

  /* Alt+↑/↓ на ручке: мышь не единственный способ работать с таблицей (§13). */
  function nudgeGroup(group: ColumnGroupCode, delta: number): void {
    const index = shown.indexOf(group)
    const next = index + delta
    if (next < 0 || next >= shown.length) return
    const reordered = [...shown]
    reordered.splice(index, 1)
    reordered.splice(next, 0, group)
    let cursor = 0
    onGroupOrder(groups.map((item) => (shownSet.has(item) ? reordered[cursor++]! : item)))
  }
}

// ─────────────────────────── блок колонок (плашка) ──────────────────────────

/**
 * Один блок: заголовок с чекбоксом и список его колонок.
 *
 * Отдельный компонент не ради порядка в файле: у каждого блока свой список
 * колонок, а значит и своё перетаскивание — хук нельзя звать в цикле.
 */
function GroupSection({
  group, title, members, visible, hiddenByWidth, collapsed, mayRemove,
  plateRef, dragging, onPlatePointerDown, onNudge,
  onToggleColumn, onToggleGroup, onColumnOrder, onMoveColumn, onRemove,
}: {
  group: ColumnGroupCode
  title: string
  members: ColumnDef[]
  visible: ReadonlySet<string>
  hiddenByWidth: readonly string[]
  collapsed: boolean
  mayRemove: boolean
  plateRef: (el: HTMLElement | null) => void
  dragging: boolean
  onPlatePointerDown: (event: ReactPointerEvent) => void
  onNudge: (delta: number) => void
  onToggleColumn: (code: string, on: boolean) => void
  onToggleGroup: () => void
  onColumnOrder: (group: string, codes: string[]) => void
  onMoveColumn: (code: string, group: string) => void
  onRemove: (target: RemoveTarget) => void
}) {
  const codes = members.map((column) => column.code)

  const rows = useDragSort({
    items: codes,
    isFixed: (code) => (FIXED_HEAD as readonly string[]).includes(code),
    onDrop: (order) => onColumnOrder(group, order),
    /* Колонку можно бросить на чужую плашку — тогда она туда и переедет. */
    onDropOutside: (code, x, y) => {
      const under = document.elementFromPoint(x, y)
      const plate = under instanceof Element ? under.closest('[data-group]') : null
      const to = plate instanceof HTMLElement ? plate.dataset.group : undefined
      if (!to || to === group) return false
      onMoveColumn(code, to)
      return true
    },
  })

  if (members.length === 0) {
    /* У пустого состояния полей свой заголовок: без него строка
       «Пользовательских полей пока нет» липла к соседней секции. */
    return (
      <div className="columns-menu__group" data-group={group}>
        <div className="columns-menu__group-head">
          <span className="t-group">{title}</span>
        </div>
        <div className="columns-menu__empty t-meta">{t.columnsMenu.fieldsEmpty}</div>
      </div>
    )
  }

  const allOn = members.every((column) => visible.has(column.code))
  const someOn = members.some((column) => visible.has(column.code))

  return (
    <div
      className={dragging ? 'columns-menu__group columns-menu__group--dragging' : 'columns-menu__group'}
      data-group={group}
      ref={plateRef}
      onPointerDown={onPlatePointerDown}
    >
      {title ? (
        <div className="columns-menu__group-head">
          <span
            className="columns-menu__grip"
            tabIndex={0}
            role="button"
            aria-label={t.columnsMenu.moveGroup(title)}
            onKeyDown={(event: KeyboardEvent) => {
              if (!event.altKey) return
              if (event.key === 'ArrowUp') { event.preventDefault(); onNudge(-1) }
              if (event.key === 'ArrowDown') { event.preventDefault(); onNudge(1) }
            }}
          >
            ⠿
          </span>
          {/* Чекбокс блока включает и выключает все его колонки разом:
              щёлкать по семи колонкам «Уровня сервиса» по одной —
              работа на ровном месте. */}
          <Checkbox
            checked={allOn}
            indeterminate={someOn && !allOn}
            label={<span className="t-group">{title}</span>}
            onChange={(checked) => {
              for (const column of members) {
                if (visible.has(column.code) !== checked) onToggleColumn(column.code, checked)
              }
            }}
          />
          <button
            type="button"
            className="columns-menu__fold t-meta"
            aria-pressed={collapsed}
            onClick={onToggleGroup}
          >
            <span aria-hidden="true">
              {collapsed ? t.group.expandShort : t.group.collapseShort}
            </span>
            <span className="visually-hidden">
              {collapsed ? t.group.expand(title) : t.group.collapse(title)}
            </span>
          </button>
          {/* Блок удаляется целиком, только если весь он собран из
              заведённых полей: встроенные колонки не удаляются. */}
          {mayRemove && members.every((column) => column.field) ? (
            <button
              type="button"
              className="columns-menu__del"
              aria-label={t.removeColumn.deleteGroup(title)}
              title={t.removeColumn.deleteGroup(title)}
              onClick={() => onRemove({
                kind: 'group',
                title,
                fields: members.map((column) => ({ id: column.field!.id, name: column.title })),
              })}
            >
              <IconTrash size={16} />
            </button>
          ) : null}
        </div>
      ) : null}

      {members.map((column) => {
        const fixed = (FIXED_HEAD as readonly string[]).includes(column.code)
        const hidden = hiddenByWidth.includes(column.code)
        return (
          <div
            key={column.code}
            ref={rows.register(column.code)}
            className={[
              'columns-menu__row',
              rows.dragging === column.code ? 'columns-menu__row--dragging' : '',
            ].filter(Boolean).join(' ')}
            onPointerDown={(event) => {
              if (fixed) return
              /* Перетаскивание строки — её дело: плашке блока знать о нём
                 незачем, иначе одно нажатие начинало бы оба переноса. */
              event.stopPropagation()
              rows.onPointerDown(column.code, event)
            }}
          >
            {fixed ? (
              <span className="columns-menu__grip columns-menu__grip--off" aria-hidden="true">⠿</span>
            ) : (
              <span
                className="columns-menu__grip"
                tabIndex={0}
                role="button"
                aria-label={t.columnsMenu.moveColumn(column.srTitle ?? column.title)}
                onKeyDown={(event: KeyboardEvent) => {
                  if (!event.altKey) return
                  const index = codes.indexOf(column.code)
                  if (event.key === 'ArrowUp' && index > 0) {
                    event.preventDefault()
                    onColumnOrder(group, moved(codes, column.code, codes[index - 1]!))
                  }
                  if (event.key === 'ArrowDown' && index < codes.length - 1) {
                    event.preventDefault()
                    onColumnOrder(group, moved(codes, column.code, codes[index + 2] ?? null))
                  }
                }}
              >
                ⠿
              </span>
            )}
            <Checkbox
              checked={visible.has(column.code)}
              onChange={(checked) => onToggleColumn(column.code, checked)}
              label={column.srTitle ?? column.title}
            />
            {hidden ? (
              <span className="columns-menu__reason t-meta">{t.columnsMenu.hiddenByWidth}</span>
            ) : null}
            {mayRemove && column.field ? (
              <button
                type="button"
                className="columns-menu__del"
                aria-label={t.removeColumn.deleteColumn(column.title)}
                title={t.removeColumn.deleteColumn(column.title)}
                onClick={() => onRemove({
                  kind: 'column',
                  title: column.title,
                  fields: [{ id: column.field!.id, name: column.title }],
                })}
              >
                <IconTrash size={16} />
              </button>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

// ───────────────────────────── новый столбец ────────────────────────────────

/**
 * Новая колонка: название, тип, блок таблицы и — для списков — справочник
 * значений.
 *
 * Раньше колонка всегда получалась текстовой и всегда падала в блок «Поля».
 * Это покрывало ровно один случай — «ещё одна заметка про бренд»; как только
 * нужен был список с выбором или своя группа колонок, дело упиралось в
 * разработчика. Справочник заводится в «Справочниках», ссылка рядом.
 *
 * Код поля человек не придумывает — он собирается из названия транслитом.
 */
function NewColumnForm({ onCancel, onCreated, catalog }: {
  onCancel: () => void
  onCreated: (code: string) => void
  catalog: ColumnDef[]
}) {
  const [name, setName] = useState('')
  const [type, setType] = useState<FieldType>('text')
  const [scope, setScope] = useState<FieldScope>('brand_channel')
  const [enumSet, setEnumSet] = useState('')
  const [group, setGroup] = useState('fields')
  const [newGroupTitle, setNewGroupTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const createField = useApp((state) => state.createField)
  const enumSets = useApp((state) => state.bootstrap?.enumSets ?? [])

  const needsEnum = type === 'select' || type === 'multiselect'
  const titles = groupTitles(catalog)
  const groups = defaultGroupOrder(catalog).filter((code) => titles[code])

  async function submit(): Promise<void> {
    const title = name.trim()
    if (title === '' || busy) return
    if (needsEnum && enumSet === '') { setError(t.columnsMenu.addNeedEnum); return }
    if (group === NEW_GROUP && newGroupTitle.trim() === '') { setError(t.columnsMenu.addNeedGroup); return }
    setBusy(true)
    setError(null)
    try {
      const custom = group === NEW_GROUP
      const code = await createField({
        name: title,
        type,
        scope,
        ...(needsEnum ? { enumSet } : null),
        ...(custom
          ? { group: `g_${translitCode(newGroupTitle)}`, groupTitle: newGroupTitle.trim() }
          : { group }),
      })
      onCreated(code)
    } catch (err) {
      setError(err instanceof Error ? err.message : t.columnsMenu.addFailed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="columns-menu__new">
      <FieldRow label={t.columnsMenu.addLabel} htmlFor="new-col-name">
        <Input
          id="new-col-name"
          value={name}
          size="m"
          autoFocus
          placeholder={t.columnsMenu.addPlaceholder}
          disabled={busy}
          onChange={(event) => { setName(event.target.value); setError(null) }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !needsEnum) { event.preventDefault(); void submit() }
            if (event.key === 'Escape') onCancel()
          }}
        />
      </FieldRow>

      <FieldRow label={t.columnsMenu.addType} htmlFor="new-col-type">
        <select
          id="new-col-type"
          className="columns-menu__select"
          value={type}
          disabled={busy}
          onChange={(event) => { setType(event.target.value as FieldType); setError(null) }}
        >
          {FIELD_TYPES.map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </select>
      </FieldRow>

      {needsEnum ? (
        <FieldRow
          label={t.columnsMenu.addEnum}
          htmlFor="new-col-enum"
          consequence={<Link to="/catalog/enums">{t.columnsMenu.addEnumLink}</Link>}
        >
          <select
            id="new-col-enum"
            className="columns-menu__select"
            value={enumSet}
            disabled={busy}
            onChange={(event) => { setEnumSet(event.target.value); setError(null) }}
          >
            <option value="">{t.columnsMenu.addEnumEmpty}</option>
            {enumSets.map((item) => (
              <option key={item.code} value={item.code}>{item.name}</option>
            ))}
          </select>
        </FieldRow>
      ) : null}

      <FieldRow label={t.columnsMenu.addGroup} htmlFor="new-col-group">
        <select
          id="new-col-group"
          className="columns-menu__select"
          value={group}
          disabled={busy}
          onChange={(event) => { setGroup(event.target.value); setError(null) }}
        >
          {groups.map((code) => (
            <option key={code} value={code}>{titles[code]}</option>
          ))}
          <option value={NEW_GROUP}>{t.columnsMenu.addGroupNew}</option>
        </select>
      </FieldRow>

      {group === NEW_GROUP ? (
        <FieldRow label={t.columnsMenu.addGroupName} htmlFor="new-col-group-name">
          <Input
            id="new-col-group-name"
            value={newGroupTitle}
            size="m"
            placeholder={t.columnsMenu.addGroupPlaceholder}
            disabled={busy}
            onChange={(event) => { setNewGroupTitle(event.target.value); setError(null) }}
          />
        </FieldRow>
      ) : null}

      <FieldRow label={t.columnsMenu.addScope} htmlFor="new-col-scope" consequence={t.columnsMenu.addScopeHint}>
        <select
          id="new-col-scope"
          className="columns-menu__select"
          value={scope}
          disabled={busy}
          onChange={(event) => setScope(event.target.value as FieldScope)}
        >
          <option value="brand_channel">{t.columnsMenu.scopeChannel}</option>
          <option value="brand">{t.columnsMenu.scopeBrand}</option>
        </select>
      </FieldRow>

      {error ? <p className="columns-menu__new-error t-meta">{error}</p> : null}
      <div className="columns-menu__new-actions">
        <Button variant="primary" size="s" disabled={name.trim() === '' || busy} onClick={() => void submit()}>
          {t.columnsMenu.addSubmit}
        </Button>
        <Button variant="ghost" size="s" disabled={busy} onClick={onCancel}>
          {t.columnsMenu.addCancel}
        </Button>
      </div>
    </div>
  )
}

/** Значение «завести новый блок» в списке блоков. */
const NEW_GROUP = '__new__'

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Текст' },
  { value: 'textarea', label: 'Текст в несколько строк' },
  { value: 'number', label: 'Число' },
  { value: 'date', label: 'Дата' },
  { value: 'bool', label: 'Да / нет' },
  { value: 'url', label: 'Ссылка' },
  { value: 'select', label: 'Список — одно значение' },
  { value: 'multiselect', label: 'Список — несколько значений' },
  { value: 'user', label: 'Человек' },
]

/** Код блока из названия: латиница, цифры и подчёркивания. */
function translitCode(name: string): string {
  const map: Record<string, string> = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i',
    й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't',
    у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '',
    э: 'e', ю: 'yu', я: 'ya',
  }
  const out = [...name.toLowerCase()].map((ch) => map[ch] ?? ch).join('')
  return out.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30) || 'group'
}
